<script setup lang="ts">
/**
 * A table's cells and settings, in the inspector, in three tabs: the cells,
 * how the table is drawn, and the table's own settings (name, caption, fit,
 * column widths), so none is found by scrolling past the others.
 *
 * The cells' tab opens with the way into the table editor. The inspector's
 * column is narrow, which suits a table of two or three columns and not much
 * more, so the editor is always one click away: the same tools at full size,
 * with the printed table beside them.
 *
 * Both are built from the same pieces, `TableFields` and `TableCellsEditor`,
 * so they offer exactly the same things.
 */
import { useI18n } from 'vue-i18n'

import { computed, watch } from 'vue'

import { useRememberedTab } from '@/composables/useRememberedTab'
import type { TableSection } from '@/domain/section'
import InspectorTabs from '@/sections/InspectorTabs.vue'
import { TAB_ICONS, type InspectorTab } from '@/sections/inspectorTabs'
import CsrdLinkPanel from '@/sections/table/CsrdLinkPanel.vue'
import TableCellsEditor from '@/sections/table/TableCellsEditor.vue'
import TableEditorDialog from '@/sections/table/TableEditorDialog.vue'
import TableFields from '@/sections/table/TableFields.vue'
import { useTableEditor } from '@/sections/table/tableEditor'

const { t } = useI18n()

const props = defineProps<{ section: TableSection }>()
const emit = defineEmits<{ change: [section: TableSection] }>()

const editor = useTableEditor()

type TableTab = 'cells' | 'look' | 'settings'

const tab = useRememberedTab<TableTab>('table', ['cells', 'look', 'settings'])

const tabs = computed<InspectorTab<TableTab>[]>(() => [
  { id: 'cells', label: t('inspector.table.tabs.cells'), icon: TAB_ICONS.cells },
  { id: 'look', label: t('inspector.table.tabs.look'), icon: TAB_ICONS.look },
  { id: 'settings', label: t('inspector.table.tabs.settings'), icon: TAB_ICONS.settings },
])

// Say that this table has an inspector for as long as it is showing, so a
// double click on the table on the page knows there is an editor to open.
watch(
  () => props.section.id,
  (id, _previous, onCleanup) => {
    onCleanup(editor.attend(id))
  },
  { immediate: true },
)
</script>

<template>
  <div class="inspector">
    <InspectorTabs v-model:open="tab" :tabs="tabs" :label="t('inspector.table.tabs.label')">
      <template #cells>
        <div class="fields">
          <CsrdLinkPanel v-if="section.csrd" :section="section" />

          <div class="open">
            <button
              type="button"
              class="b-btn b-btn--outline open__button"
              @click="editor.open(section.id)"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M9.5 2.5h4v4M13.5 2.5 9 7M6.5 13.5h-4v-4M2.5 13.5 7 9"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {{ t('inspector.table.openEditor') }}
            </button>
            <span class="open__hint">{{ t('inspector.table.openEditorHint') }}</span>
          </div>

          <TableCellsEditor :section="section" @change="emit('change', $event)" />
        </div>
      </template>

      <template #look>
        <TableFields :section="section" part="look" @change="emit('change', $event)" />
      </template>

      <template #settings>
        <TableFields :section="section" part="settings" @change="emit('change', $event)" />
      </template>
    </InspectorTabs>

    <TableEditorDialog
      :open="editor.openId.value === section.id"
      :section="section"
      @change="emit('change', $event)"
      @close="editor.close()"
    />
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.open {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.open__button {
  gap: var(--sp-3);
  width: 100%;
}

.open__hint {
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.45;
}
</style>
